import { TourTelemetryEvent } from '../types/tour';
import { logger } from '../utils/logger';

export class TourTelemetry {
  private static events: TourTelemetryEvent[] = [];
  private static isEnabled: boolean = true;
  private static maxEvents: number = 100;

  /**
   * Configura a telemetria
   */
  public static configure(options: {
    enabled?: boolean;
    maxEvents?: number;
  }): void {
    this.isEnabled = options.enabled ?? true;
    this.maxEvents = options.maxEvents ?? 100;
    
    logger.debug(`[TourTelemetry] Configurado - Enabled: ${this.isEnabled}, MaxEvents: ${this.maxEvents}`);
  }

  /**
   * Registra um evento de telemetria
   */
  public static trackEvent(
    event: TourTelemetryEvent['event'],
    data: Omit<TourTelemetryEvent, 'event' | 'timestamp'> = {}
  ): void {
    if (!this.isEnabled) {
      return;
    }

    const telemetryEvent: TourTelemetryEvent = {
      event,
      timestamp: Date.now(),
      ...data
    };

    // Adicionar evento à lista
    this.events.push(telemetryEvent);

    // Manter apenas os últimos N eventos
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Log do evento
    logger.info(`[TourTelemetry] Evento registrado: ${event}`, {
      stepId: data.stepId,
      stepIndex: data.stepIndex,
      actionType: data.actionType,
      userId: data.userId,
      tourVersion: data.tourVersion
    });

    // Enviar para analytics (se configurado)
    this.sendToAnalytics(telemetryEvent);
  }

  /**
   * Registra início do tour
   */
  public static trackTourStart(tourVersion: string, userId?: string): void {
    this.trackEvent('tour_start', {
      tourVersion,
      userId,
      metadata: {
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Registra visualização de step
   */
  public static trackStepView(
    stepId: string,
    stepIndex: number,
    tourVersion: string,
    userId?: string,
    metadata?: Record<string, any>
  ): void {
    this.trackEvent('step_view', {
      stepId,
      stepIndex,
      tourVersion,
      userId,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Registra conclusão de ação em step
   */
  public static trackStepActionCompleted(
    stepId: string,
    stepIndex: number,
    actionType: TourTelemetryEvent['actionType'],
    tourVersion: string,
    userId?: string,
    metadata?: Record<string, any>
  ): void {
    this.trackEvent('step_action_completed', {
      stepId,
      stepIndex,
      actionType,
      tourVersion,
      userId,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Registra conclusão do tour
   */
  public static trackTourFinish(
    tourVersion: string,
    userId?: string,
    metadata?: Record<string, any>
  ): void {
    const tourDuration = this.calculateTourDuration();
    
    this.trackEvent('tour_finish', {
      tourVersion,
      userId,
      metadata: {
        ...metadata,
        tourDuration,
        totalSteps: this.getTotalStepsViewed(),
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Registra quando o tour é pulado
   */
  public static trackTourSkip(
    tourVersion: string,
    userId?: string,
    stepIndex?: number,
    metadata?: Record<string, any>
  ): void {
    const tourDuration = this.calculateTourDuration();
    
    this.trackEvent('tour_skip', {
      stepIndex,
      tourVersion,
      userId,
      metadata: {
        ...metadata,
        tourDuration,
        stepsCompleted: this.getTotalStepsViewed(),
        skippedAtStep: stepIndex,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Registra erro no tour
   */
  public static trackTourError(
    error: Error | string,
    tourVersion: string,
    userId?: string,
    stepId?: string,
    stepIndex?: number,
    metadata?: Record<string, any>
  ): void {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorStack = typeof error === 'object' ? error.stack : undefined;

    this.trackEvent('tour_error', {
      stepId,
      stepIndex,
      tourVersion,
      userId,
      metadata: {
        ...metadata,
        error: errorMessage,
        errorStack,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Calcula a duração do tour atual
   */
  private static calculateTourDuration(): number {
    const startEvent = this.events.find(e => e.event === 'tour_start');
    if (!startEvent) {
      return 0;
    }

    return Date.now() - startEvent.timestamp;
  }

  /**
   * Conta o total de steps visualizados
   */
  private static getTotalStepsViewed(): number {
    const stepViewEvents = this.events.filter(e => e.event === 'step_view');
    return stepViewEvents.length;
  }

  /**
   * Envia evento para sistema de analytics
   */
  private static sendToAnalytics(event: TourTelemetryEvent): void {
    try {
      // Google Analytics 4 (se disponível)
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', event.event, {
          event_category: 'interactive_tour',
          event_label: event.stepId,
          custom_parameter_step_index: event.stepIndex,
          custom_parameter_action_type: event.actionType,
          custom_parameter_tour_version: event.tourVersion,
          custom_parameter_user_id: event.userId
        });
      }

      // Amplitude (se disponível)
      if (typeof window !== 'undefined' && (window as any).amplitude) {
        (window as any).amplitude.track(`Tour ${event.event}`, {
          stepId: event.stepId,
          stepIndex: event.stepIndex,
          actionType: event.actionType,
          tourVersion: event.tourVersion,
          userId: event.userId,
          ...event.metadata
        });
      }

      // Mixpanel (se disponível)
      if (typeof window !== 'undefined' && (window as any).mixpanel) {
        (window as any).mixpanel.track(`Tour ${event.event}`, {
          stepId: event.stepId,
          stepIndex: event.stepIndex,
          actionType: event.actionType,
          tourVersion: event.tourVersion,
          userId: event.userId,
          ...event.metadata
        });
      }

      // Custom analytics endpoint (se configurado)
      const analyticsEndpoint = process.env.REACT_APP_TOUR_ANALYTICS_ENDPOINT;
      if (analyticsEndpoint) {
        fetch(analyticsEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(event)
        }).catch(error => {
          logger.warn('[TourTelemetry] Erro ao enviar para analytics endpoint:', error);
        });
      }

    } catch (error) {
      logger.warn('[TourTelemetry] Erro ao enviar evento para analytics:', error);
    }
  }

  /**
   * Obtém todos os eventos registrados
   */
  public static getEvents(): TourTelemetryEvent[] {
    return [...this.events];
  }

  /**
   * Obtém eventos filtrados por tipo
   */
  public static getEventsByType(eventType: TourTelemetryEvent['event']): TourTelemetryEvent[] {
    return this.events.filter(e => e.event === eventType);
  }

  /**
   * Obtém estatísticas do tour atual
   */
  public static getTourStats(): {
    totalEvents: number;
    tourStarted: boolean;
    tourCompleted: boolean;
    tourSkipped: boolean;
    stepsViewed: number;
    actionsCompleted: number;
    errors: number;
    duration?: number;
  } {
    const events = this.events;
    
    return {
      totalEvents: events.length,
      tourStarted: events.some(e => e.event === 'tour_start'),
      tourCompleted: events.some(e => e.event === 'tour_finish'),
      tourSkipped: events.some(e => e.event === 'tour_skip'),
      stepsViewed: events.filter(e => e.event === 'step_view').length,
      actionsCompleted: events.filter(e => e.event === 'step_action_completed').length,
      errors: events.filter(e => e.event === 'tour_error').length,
      duration: this.calculateTourDuration()
    };
  }

  /**
   * Limpa todos os eventos registrados
   */
  public static clearEvents(): void {
    this.events = [];
    logger.debug('[TourTelemetry] Eventos limpos');
  }

  /**
   * Exporta eventos para JSON
   */
  public static exportEvents(): string {
    return JSON.stringify(this.events, null, 2);
  }

  /**
   * Importa eventos de JSON
   */
  public static importEvents(jsonData: string): void {
    try {
      const events = JSON.parse(jsonData) as TourTelemetryEvent[];
      this.events = events;
      logger.debug(`[TourTelemetry] ${events.length} eventos importados`);
    } catch (error) {
      logger.error('[TourTelemetry] Erro ao importar eventos:', error);
    }
  }

  /**
   * Habilita/desabilita telemetria
   */
  public static setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    logger.debug(`[TourTelemetry] Telemetria ${enabled ? 'habilitada' : 'desabilitada'}`);
  }

  /**
   * Verifica se a telemetria está habilitada
   */
  public static getIsEnabled(): boolean {
    return this.isEnabled;
  }
}
